
import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ArrowRight, CheckCircle, Calendar, Users, Building, Target, TrendingUp, Shield, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { caseStudies } from "@/data/caseStudyData";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";
import { BreadcrumbNav } from "@/components/case-studies/BreadcrumbNav";

const FinancialServicesContinuity = () => {
  const caseStudy = caseStudies.find(study => study.id === "financial-services-business-continuity");
  const companyName = caseStudy?.redactedName || "Pacific Financial Group";
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="py-12 md:py-24 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <BreadcrumbNav currentPage="Regional Bank Ensures Business Continuity" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="mb-6">
                  <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    Case Study: Financial Services
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  How <RedactedCompanyName companyName={companyName} /> Ensures Business Continuity While Meeting Regulations
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Learn how we helped a financial services firm implement high-availability infrastructure, enhance security compliance, and eliminate business disruptions.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/get-started">
                    <Button 
                      size="lg"
                      className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 shadow-md group"
                    >
                      Get Similar Results
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <a href="#challenge">
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-purple-300 text-purple-800 hover:bg-purple-50"
                    >
                      Read the Case Study
                    </Button>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <Card className="bg-white border-0 shadow-xl rounded-xl overflow-hidden">
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Industry</p>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Financial Services</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company Size</p>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">150+ Employees</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Project Duration</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">3 Months</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Focus Areas</p>
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Business Continuity & Compliance</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-3">Key Results:</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">99.99% system uptime achieved</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">Zero critical findings in regulatory audits</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">95% reduction in recurring IT issues</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge Section */}
        <section id="challenge" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-4">
                  The Challenge
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  System Instability and Compliance Concerns
                </h2>
                <p className="text-lg text-gray-600">
                  <RedactedCompanyName companyName={companyName} /> experienced regular system instability and compliance concerns, causing significant operational and financial challenges.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">System Outages</h3>
                  <p className="text-gray-600 mb-4">
                    Critical client financial operations were encountering frequent system failures, causing significant financial and reputational damage.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">System downtime costing estimated $25,000 per hour</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Regulatory Compliance</h3>
                  <p className="text-gray-600 mb-4">
                    IT audits revealed significant gaps in meeting FFIEC and GLBA regulatory requirements, putting the company at risk for penalties.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Multiple regulatory findings requiring costly remediation</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Vulnerabilities</h3>
                  <p className="text-gray-600 mb-4">
                    Outdated security infrastructure created vulnerabilities for sensitive customer financial data, creating significant business risk.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Potential data breaches threatening customer trust</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Limited IT Resources</h3>
                  <p className="text-gray-600 mb-4">
                    Internal IT team was overwhelmed and lacked specialized expertise in financial-grade systems and regulatory compliance.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Reactive approach to IT creating constant fire-fighting</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 p-8 rounded-xl border border-red-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-red-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Breaking Point
                    </h3>
                    <p className="text-gray-700">
                      "We were experiencing repeated system failures that directly impacted our clients and put our compliance status at risk. We needed a partner who could deliver both financial-grade reliability and regulatory compliance expertise."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— CIO at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Solution Section */}
        <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  The Solution
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Comprehensive Business Continuity Solution
                </h2>
                <p className="text-lg text-gray-600">
                  Our approach focused on building a robust, compliant infrastructure with multiple layers of protection and monitoring.
                </p>
              </div>
              
              <div className="space-y-8 mb-12">
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Target className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">High-Availability Infrastructure</h3>
                      <p className="text-gray-600 mb-4">
                        We implemented a comprehensive high-availability infrastructure with redundant components and geographic distribution to eliminate single points of failure.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Redundant server and network architecture with automatic failover</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Geographically distributed backup systems for disaster recovery</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Load-balanced systems to handle peak transaction periods</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Regulatory-Focused Security Framework</h3>
                      <p className="text-gray-600 mb-4">
                        We designed and implemented a multi-layered security framework specifically aligned with FFIEC and GLBA requirements.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Comprehensive security controls mapped to regulatory requirements</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Advanced encryption for data at rest and in transit</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Automated security compliance reporting for audits</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">24/7 Proactive Monitoring & Management</h3>
                      <p className="text-gray-600 mb-4">
                        We implemented continuous security monitoring with rapid incident response capability to prevent and mitigate potential issues.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Real-time monitoring of all critical systems and security controls</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Proactive issue resolution before business impact occurs</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Dedicated team for financial services security expertise</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 p-8 rounded-xl border border-purple-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-purple-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Transformation Moment
                    </h3>
                    <p className="text-gray-700">
                      "The moment we realized the transformation was when we experienced a power outage at our primary location, but our systems automatically failed over to the backup site with zero disruption. Our clients didn't even notice. That level of reliability is exactly what we needed in financial services."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— COO at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  The Results
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Measurable Reliability & Compliance Improvements
                </h2>
                <p className="text-lg text-gray-600">
                  Within three months, <RedactedCompanyName companyName={companyName} /> achieved transformative improvements in both system reliability and regulatory compliance.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">99.99</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">System Availability</h3>
                  <p className="text-gray-600">
                    Achieved near-perfect uptime for critical financial systems, eliminating costly business disruptions and maintaining client trust.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">100</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Compliance Achievement</h3>
                  <p className="text-gray-600">
                    Passed all regulatory IT audits with zero critical findings, fully satisfying FFIEC and GLBA requirements.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">&lt;4</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">hrs</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Recovery Time</h3>
                  <p className="text-gray-600">
                    Reduced potential system recovery time from days to under 4 hours, ensuring business continuity even in worst-case scenarios.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">95</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">IT Issue Reduction</h3>
                  <p className="text-gray-600">
                    Dramatically reduced recurring IT issues through proactive monitoring and maintenance, improving operational efficiency.
                  </p>
                </div>
              </div>
              
              <div className="bg-green-50 p-8 rounded-xl border border-green-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Long-Term Impact
                    </h3>
                    <p className="text-gray-700">
                      "In financial services, system reliability isn't just about convenience—it's about maintaining customer trust. edgefi understood our unique needs and delivered a managed IT solution that ensures we're always operational, secure, and compliant."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— COO at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Ready to Ensure Your Business Continuity?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Schedule a consultation to discover how we can help your organization achieve similar results with a customized managed IT solution.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/get-started">
                  <Button 
                    size="lg"
                    className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 transition-colors px-8 py-6 h-auto shadow-lg hover:shadow-xl group"
                  >
                    Schedule Your Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/services/managed-services">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-6 h-auto"
                  >
                    Explore Managed IT Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FinancialServicesContinuity;
